import { OverlayRef } from '@angular/cdk/overlay';
import { DestroyRef, Directive, OnInit, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
    AppInfoConfig,
    BalancePropertiesCoreService,
    CashierService,
    CommonMessages,
    DeviceService,
    ElementRepositoryService,
    EventsService,
    Logger,
    MenuItemsService,
    MenuSection,
    NativeAppService,
    NativeEventType,
    NavigationService,
    Page,
    UserService,
    UtilsService,
    VanillaElements,
    VanillaEventNames,
    WINDOW,
} from '@frontend/vanilla/core';
import { QuickDepositMessageService } from '@payments/quickdeposit-feature';
import { firstValueFrom } from 'rxjs';

import { CashierIframeAction, CashierIframeEvent, CashierIframeType } from '../cashier-iframe.models';
import { CashierConfig } from '../cashier.client-config';
import { QuickDepositOptions } from './quick-deposit.models';
import { QUICK_DEPOSIT_OPTIONS } from './quick-deposit.service';

@Directive()
export class QuickDepositResponsiveComponent implements OnInit {
    protected readonly deviceService = inject(DeviceService);
    protected readonly commonMessages = inject(CommonMessages);
    protected readonly cashierService = inject(CashierService);
    protected readonly elementRepositoryService = inject(ElementRepositoryService);
    protected readonly cashierConfig = inject(CashierConfig);
    protected readonly user = inject(UserService);
    protected readonly page = inject(Page);
    protected readonly appInfoConfig = inject(AppInfoConfig);
    protected readonly utils = inject(UtilsService);
    protected readonly overlayRef = inject(OverlayRef);
    protected readonly menuItemsService = inject(MenuItemsService);
    protected readonly quickDepositMessageService = inject(QuickDepositMessageService);
    protected readonly logger = inject(Logger);
    protected readonly balancePropertiesService = inject(BalancePropertiesCoreService);
    protected readonly navigationService = inject(NavigationService);
    protected readonly eventsService = inject(EventsService);
    protected readonly nativeAppService = inject(NativeAppService);
    protected readonly options = inject<QuickDepositOptions>(QUICK_DEPOSIT_OPTIONS);

    readonly url = signal<string>('');
    readonly VanillaElements = VanillaElements;
    readonly #destroyRef = inject(DestroyRef);
    readonly #window = inject(WINDOW);

    constructor() {
        this.listenMessagesFromQd();
    }

    async ngOnInit() {
        await firstValueFrom(this.cashierConfig.whenReady);

        if (!this.cashierConfig.isPaymentsAppQuickDepositEnabled) {
            let urlTemplate = this.cashierConfig.quickDepositUrlTemplate;

            if (this.cashierConfig.singleSignOnIntegrationType === 'cookie') {
                urlTemplate = urlTemplate.replace('&sessionKey={5}', ''); // TODO: update dynacon entries and remove sessionKey={5} from them once singleSignOnIntegrationType is cookie everywhere. also remove this line after.
            }
            const productValue = this.cashierService.getProductValue();

            this.url.set(
                this.utils.format(
                    this.cashierConfig.host + urlTemplate,
                    this.user.id,
                    this.appInfoConfig.brand,
                    productValue,
                    this.appInfoConfig.channel,
                    this.page.lang,
                    this.user.ssoToken, // TODO: remove this line once singleSignOnIntegrationType is cookie everywhere
                    encodeURIComponent(this.#window.location.href),
                ),
            );

            if (this.options.showKYCVerifiedMessage) {
                this.url.update((url: string) => `${url}&showKYCVerifiedMessage=true`);
            }
        }
    }

    close(message: CashierIframeEvent) {
        if (message['showAllOptions'] === 'true') {
            this.cashierService.goToCashierDeposit({ skipQuickDeposit: true });
        } else {
            this.menuItemsService.setActive(MenuSection.Menu, null);
        }
        this.overlayRef.detach();
    }

    resize(message: CashierIframeEvent) {
        try {
            const element = this.elementRepositoryService
                .get(VanillaElements.QUICK_DEPOSIT_WRAPPER)
                ?.querySelector<HTMLElement>('.player-quickdeposit-iframe-wrapper');

            if (element?.style && (!message.target || message.target.toLowerCase() === CashierIframeType.QuickDeposit)) {
                const isNotMobile = !this.deviceService.isMobilePhone;
                const parsedWidth = isNotMobile && message['width'] && message['width'].length > 0 ? `${parseInt(message['width'], 10)}px` : null;
                const parsedHeight = message['height'] && message['height'].length > 0 ? `${parseInt(message['height'], 10)}px` : null;

                // mobile phone and v2: theme specify width 100%
                if (parsedWidth != null) {
                    element.style.width = parsedWidth;
                }

                if (parsedHeight != null) {
                    element.style.height = parsedHeight;
                }
            }
        } catch (error: any) {
            this.logger.warn(`Failed to resize.`, error);
        }
    }

    private listenMessagesFromQd() {
        if (this.cashierConfig.isPaymentsAppQuickDepositEnabled) {
            effect(() => {
                const outputMessage = this.quickDepositMessageService.output();
                if (outputMessage) {
                    const message: CashierIframeEvent = outputMessage.split('&').reduce((seed: Record<string, string>, cur: string) => {
                        const param = cur.trim().split('=');

                        if (param[0] && param[1]) {
                            seed[param[0]] = param[1];
                        }

                        return seed;
                    }, {});

                    switch (message.action?.toUpperCase()) {
                        case CashierIframeAction.Close:
                            this.close(message);
                            break;
                        case CashierIframeAction.Resize:
                            this.resize(message);
                            break;
                        case CashierIframeAction.UpdateBalance:
                            this.balancePropertiesService.whenReady
                                .pipe(takeUntilDestroyed(this.#destroyRef))
                                .subscribe(() => this.balancePropertiesService.refresh());
                            break;
                        case CashierIframeAction.Chat:
                            this.close(message);
                            const url = this.navigationService.location.clone();
                            url.search.set('cashierId', message['Web_site_URL']);
                            url.search.set('chat', 'open');
                            this.navigationService.goTo(url);
                            break;
                        case CashierIframeAction.DepositSuccess:
                            this.eventsService.raise({
                                eventName: VanillaEventNames.DepositSuccessQD,
                                data: { currency: message['currency'], value: message['amount'] },
                            });
                            this.close(message);
                            break;
                        case CashierIframeAction.FetchLocation:
                            this.nativeAppService.sendToNative({
                                eventName: NativeEventType.TRIGGER_GEO_LOCATION,
                                parameters: message,
                            });
                            break;
                    }
                }
            });
        }
    }
}
